import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Flex,
  Card,
  CardBody,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Button,
  Badge,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import {
  FaBullseye,
  FaStar,
  FaGlobe,
  FaArrowRight,
  FaTrophy,
  FaChartLine,
  FaHeart,
  FaSmile,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLightbulb,
  FaCrown,
  FaSearch,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getReviews } from '../../services/reviewService';
import { getYearData } from '../../services/yearService';
import { useUser } from '../../contexts/UserContext';
import { logger } from '../../utils/logger';
import { SectionLabel } from '../dashboards/DashboardWrapper';
import { getTrafficValue, formatNumber } from '../../utils/competitiveIntelHelpers';

// Consistent color palette for favorites (green, orange, red, then others - no purple)
const FAVORITE_COLORS = [
  '#38A169', // Green (1st favorite)
  '#DD6B20', // Orange (2nd favorite)
  '#E53E3E', // Red (3rd favorite)
  '#3182CE', // Blue (4th favorite)
  '#319795', // Teal (5th favorite)
  '#D69E2E', // Yellow (6th favorite)
  '#D53F8C', // Pink (7th favorite)
  '#00B5D8', // Cyan (8th favorite)
];

// Generate a consistent color for a business based on its name
const getConsistentColor = (name, allNames) => {
  if (!name || !allNames || allNames.length === 0) return FAVORITE_COLORS[0];
  const sortedNames = [...allNames].sort();
  const index = sortedNames.indexOf(name);
  if (index === -1) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash = hash & hash;
    }
    return FAVORITE_COLORS[Math.abs(hash) % FAVORITE_COLORS.length];
  }
  return FAVORITE_COLORS[index % FAVORITE_COLORS.length];
};

// Helper function to calculate SEO Score
const calculateSeoScore = (business) => {
  const visitors = business.traffic_data?.metrics?.estimated_monthly_visitors || 0;
  if (visitors === 0) return 0;
  let rankForSeo = business.query_ranking_info?.url_ranking;
  if (typeof rankForSeo === 'number' && rankForSeo > 0 && rankForSeo <= 100) {
    return visitors / rankForSeo;
  }
  return visitors / 100;
};

// Helper function to calculate avg reviews per month
const calculateAvgMonthlyReviews = (business) => {
  const counts = Object.values(business?.monthlyReviewCounts || business?.historicalData?.monthlyReviewCounts || {});
  if (!counts.length) return 0;
  return counts.reduce((a, b) => a + b, 0) / counts.length;
};

// Helper function to calculate sentiment score
const calculateSentimentScore = (breakdown) => {
  if (!breakdown) return 0;
  const { positive = 0, neutral = 0, negative = 0 } = breakdown;
  const total = positive + neutral + negative;
  return total > 0 ? ((positive + neutral * 0.5) / total) * 100 : 0;
};


// ============================================================================
// BENCHMARK CARD COMPONENT
// ============================================================================
const BenchmarkCard = ({ icon, label, value, unit, color }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      shadow="lg"
      borderRadius="2xl"
      overflow="hidden"
      position="relative"
      _hover={{
        transform: 'translateY(-4px) scale(1.02)',
        shadow: `xl`,
        borderColor: `${color}.400`,
      }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bgGradient: `linear(to-r, ${color}.400, ${color}.600)`,
      }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      cursor="default"
    >
      <CardBody p={5}>
        <HStack spacing={4} align="center">
          {/* Icon */}
          <Flex
            w="56px"
            h="56px"
            borderRadius="xl"
            bg={statBg}
            justify="center"
            align="center"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <Icon as={icon} color={`${color}.500`} boxSize={6} />
          </Flex>

          {/* Content */}
          <VStack spacing={0} align="start" flex={1}>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color={labelColor}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {label}
            </Text>
            <Text
              fontSize="3xl"
              fontWeight="extrabold"
              bgGradient={`linear(to-r, ${color}.500, ${color}.700)`}
              bgClip="text"
              lineHeight="1.2"
            >
              {value}
            </Text>
            <Text fontSize="xs" color={labelColor} fontWeight="medium">
              {unit}
            </Text>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

// ============================================================================
// INSIGHT BOX COMPONENT
// ============================================================================
const InsightBox = ({ type, title, children }) => {
  // All hooks must be called unconditionally at the top
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const positiveBg = useColorModeValue('green.50', 'green.900');
  const positiveTitleColor = useColorModeValue('green.700', 'green.300');
  const negativeBg = useColorModeValue('red.50', 'red.900');
  const negativeTitleColor = useColorModeValue('red.700', 'red.300');
  const neutralBg = useColorModeValue('blue.50', 'blue.900');
  const neutralTitleColor = useColorModeValue('blue.700', 'blue.300');

  const configs = {
    positive: {
      bg: positiveBg,
      borderColor: 'green.400',
      icon: FaCheckCircle,
      iconColor: 'green.500',
      titleColor: positiveTitleColor,
    },
    negative: {
      bg: negativeBg,
      borderColor: 'red.400',
      icon: FaExclamationTriangle,
      iconColor: 'red.500',
      titleColor: negativeTitleColor,
    },
    neutral: {
      bg: neutralBg,
      borderColor: 'blue.400',
      icon: FaLightbulb,
      iconColor: 'blue.500',
      titleColor: neutralTitleColor,
    },
  };

  const config = configs[type] || configs.neutral;

  return (
    <Box
      bg={config.bg}
      borderLeft="5px solid"
      borderColor={config.borderColor}
      borderRadius="xl"
      p={5}
      mt={4}
      boxShadow="md"
      transition="all 0.2s ease"
      _hover={{
        shadow: 'lg',
        transform: 'translateX(4px)',
      }}
    >
      <HStack spacing={3} mb={3}>
        <Flex
          w="28px"
          h="28px"
          borderRadius="lg"
          bg={`${config.iconColor.replace('.500', '.100')}`}
          justify="center"
          align="center"
        >
          <Icon as={config.icon} color={config.iconColor} boxSize={4} />
        </Flex>
        <Text fontWeight="bold" color={config.titleColor} fontSize="md">
          {title}
        </Text>
      </HStack>
      <Text fontSize="sm" color={textColor} lineHeight="1.7" ml={10}>
        {children}
      </Text>
    </Box>
  );
};

// ============================================================================
// RANKINGS MODAL COMPONENT
// ============================================================================
const RankingsModal = ({ isOpen, onClose, title, rankings, target, unit, targetPosition, metricKey }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const stripeBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent bg={cardBg} maxH="80vh">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>{title}</Text>
            <HStack spacing={4}>
              <Badge colorScheme="blue" fontSize="xs">Target: {target}{unit}</Badge>
              <Text fontSize="sm" color={subtleTextColor}>Top {targetPosition} threshold</Text>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={4}>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color={subtleTextColor}>Rank</Th>
                  <Th color={subtleTextColor}>Business</Th>
                  <Th isNumeric color={subtleTextColor}>Value</Th>
                  <Th color={subtleTextColor}>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rankings.slice(0, 20).map((item, index) => {
                  const value = item[metricKey] || 0;
                  const meetsTarget = value >= target;
                  const isInTargetRange = index < targetPosition;
                  
                  return (
                    <Tr 
                      key={index} 
                      bg={index % 2 === 0 ? 'transparent' : stripeBg}
                      borderLeftWidth={isInTargetRange ? '3px' : '0'}
                      borderLeftColor="green.400"
                    >
                      <Td>
                        <Badge 
                          colorScheme={isInTargetRange ? 'green' : 'gray'}
                          fontSize="xs"
                        >
                          #{index + 1}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                          {item.name}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" fontWeight="bold" color={textColor}>
                          {typeof value === 'number' ? value.toLocaleString() : value}{unit}
                        </Text>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={meetsTarget ? 'green' : 'red'} 
                          fontSize="xs"
                        >
                          {meetsTarget ? 'On Target' : 'Below'}
                        </Badge>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {rankings.length > 20 && (
            <Text fontSize="xs" color={subtleTextColor} textAlign="center" mt={4}>
              Showing top 20 of {rankings.length} businesses
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ============================================================================
// HORIZONTAL BAR CHART COMPONENT (NEW DESIGN)
// ============================================================================
const HorizontalBarChart = ({
  data,
  target,
  title,
  subtitle,
  icon,
  iconColor,
  unit,
  allFavoriteNames,
  enrichedFavorites,
  targetPosition,
  rankings,
  metricKey,
  onViewAll,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const trackBg = useColorModeValue('gray.100', 'gray.700');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map(d => d.value), target) * 1.15;

  // Generate insight message
  const getInsight = () => {
    const ahead = data.filter(d => d.meetsTarget);
    const behind = data.filter(d => !d.meetsTarget);
    
    if (ahead.length === data.length) {
      return {
        type: 'positive',
        title: '✅ Good News!',
        message: `All ${data.length} of your favorites already exceed the ${target}${unit} target needed for Top ${targetPosition}. Keep up the great work!`
      };
    } else if (behind.length === data.length) {
      const biggestGap = Math.max(...behind.map(d => target - d.value));
      const needsWork = behind.find(d => target - d.value === biggestGap);
      return {
        type: 'negative',
        title: '⚠️ Opportunity Alert',
        message: `${needsWork?.fullName} needs to improve from ${needsWork?.value}${unit} to ${target}${unit} (gap of ${Math.round(biggestGap)}${unit}) to reach Top ${targetPosition}.`
      };
    } else {
      return {
        type: 'neutral',
        title: '💡 What This Means',
        message: `${ahead.length} favorite${ahead.length > 1 ? 's' : ''} ${ahead.length > 1 ? 'are' : 'is'} meeting the target while ${behind.length} need${behind.length === 1 ? 's' : ''} improvement. The red target line shows the ${target}${unit} benchmark for Top ${targetPosition}.`
      };
    }
  };

  const insight = getInsight();

  return (
    <Card 
      bg={cardBg} 
      borderWidth="1px" 
      borderColor={borderColor} 
      shadow="xl"
      w="100%"
      borderRadius="2xl"
      overflow="hidden"
      _hover={{
        shadow: '2xl',
        transform: 'translateY(-2px)',
      }}
      transition="all 0.3s ease"
    >
      <CardBody p={8}>
        {/* Header */}
        <Flex justify="space-between" align="start" mb={6}>
          <HStack spacing={4}>
            <Flex
              w="52px"
              h="52px"
              borderRadius="xl"
              bg={statBg}
              justify="center"
              align="center"
              boxShadow="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Icon as={icon} color={iconColor} boxSize={6} />
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="md" color={textColor} fontWeight="bold">{title}</Heading>
              <Text fontSize="sm" color={subtleTextColor}>{subtitle}</Text>
            </VStack>
          </HStack>
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={4}
            py={2}
            borderRadius="full"
            fontWeight="bold"
            boxShadow="md"
          >
            Target: {target}{unit}
          </Badge>
        </Flex>

        {/* Bars */}
        <VStack spacing={6} align="stretch">
          {data.map((item, index) => {
            const barWidth = Math.max((item.value / maxValue) * 100, 2);
            const targetLinePosition = (target / maxValue) * 100;
            const difference = item.value - target;
            const isFavorite = enrichedFavorites?.some(f => f.name === item.fullName);
            const favoriteData = enrichedFavorites?.find(f => f.name === item.fullName);
            const rank = favoriteData?.reviewRank;
            const barColor = getConsistentColor(item.fullName, allFavoriteNames);

            return (
              <Box key={index}>
                {/* Business Info Row */}
                <Flex justify="space-between" align="center" mb={3}>
                  <HStack spacing={3} minW="200px" maxW="300px">
                    {rank && (
                      <Badge
                        colorScheme={rank <= targetPosition ? 'green' : 'gray'}
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontWeight="bold"
                        boxShadow="sm"
                      >
                        #{rank}
                      </Badge>
                    )}
                    <Text
                      fontSize="md"
                      fontWeight="semibold"
                      color={textColor}
                      noOfLines={1}
                    >
                      {item.fullName}
                    </Text>
                    {isFavorite && (
                      <Icon as={FaHeart} color="red.400" boxSize={4} />
                    )}
                    {rank === 1 && (
                      <Icon as={FaCrown} color="yellow.500" boxSize={4} />
                    )}
                  </HStack>
                  
                  {/* Status Badge with enhanced styling */}
                  <Badge
                    colorScheme={item.meetsTarget ? 'green' : 'red'}
                    fontSize="sm"
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontWeight="bold"
                    boxShadow={item.meetsTarget 
                      ? '0 4px 12px rgba(72, 187, 120, 0.3)' 
                      : '0 4px 12px rgba(245, 101, 101, 0.3)'
                    }
                    transition="all 0.2s ease"
                    _hover={{
                      transform: 'scale(1.05)',
                    }}
                  >
                    {item.meetsTarget ? (
                      <>+{Math.abs(difference).toFixed(difference % 1 === 0 ? 0 : 1)}{unit} ahead</>
                    ) : (
                      <>{Math.abs(difference).toFixed(difference % 1 === 0 ? 0 : 1)}{unit} behind</>
                    )}
                  </Badge>
                </Flex>

                {/* Bar Track */}
                <Box position="relative" h="52px" mt={2}>
                  {/* Background Track with inner shadow */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    h="52px"
                    bg={trackBg}
                    borderRadius="2xl"
                    boxShadow="inset 0 4px 12px rgba(0,0,0,0.08), inset 0 2px 4px rgba(0,0,0,0.06)"
                    overflow="hidden"
                  />
                  
                  {/* Filled Bar with gradient, glow and glass effect */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    h="52px"
                    w={`${barWidth}%`}
                    bgGradient={`linear(to-r, ${barColor}, ${barColor}dd, ${barColor})`}
                    borderRadius="2xl"
                    transition="all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    pr={4}
                    boxShadow={`
                      0 8px 32px ${barColor}40,
                      0 4px 16px ${barColor}30,
                      0 2px 8px rgba(0,0,0,0.15),
                      inset 0 2px 4px rgba(255,255,255,0.25),
                      inset 0 -2px 4px rgba(0,0,0,0.1)
                    `}
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '50%',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)',
                      borderRadius: '2xl',
                      pointerEvents: 'none',
                    }}
                    _after={{
                      content: '""',
                      position: 'absolute',
                      top: '4px',
                      left: '8px',
                      right: '8px',
                      height: '8px',
                      background: 'linear-gradient(to right, rgba(255,255,255,0.3), rgba(255,255,255,0.1), rgba(255,255,255,0.3))',
                      borderRadius: 'full',
                      filter: 'blur(2px)',
                      pointerEvents: 'none',
                    }}
                    _hover={{
                      transform: 'scaleY(1.03)',
                      boxShadow: `
                        0 12px 40px ${barColor}50,
                        0 6px 20px ${barColor}40,
                        0 3px 10px rgba(0,0,0,0.2),
                        inset 0 2px 4px rgba(255,255,255,0.3),
                        inset 0 -2px 4px rgba(0,0,0,0.1)
                      `,
                    }}
                  >
                    <Text
                      fontSize="lg"
                      fontWeight="extrabold"
                      color="white"
                      textShadow="0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)"
                      letterSpacing="tight"
                    >
                      {item.value}{unit}
                    </Text>
                  </Box>

                  {/* Target Line with glow effect */}
                  <Box
                    position="absolute"
                    top="-8px"
                    left={`${targetLinePosition}%`}
                    transform="translateX(-50%)"
                    h="68px"
                    w="4px"
                    bgGradient="linear(to-b, red.400, red.600, red.400)"
                    borderRadius="full"
                    zIndex={2}
                    boxShadow="0 0 12px rgba(229, 62, 62, 0.6), 0 0 24px rgba(229, 62, 62, 0.3)"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '0',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '10px',
                      height: '10px',
                      background: 'red.500',
                      borderRadius: 'full',
                      boxShadow: '0 0 8px rgba(229, 62, 62, 0.8)',
                    }}
                  />
                  <Text
                    position="absolute"
                    top="-28px"
                    left={`${targetLinePosition}%`}
                    transform="translateX(-50%)"
                    fontSize="xs"
                    fontWeight="black"
                    color="red.500"
                    whiteSpace="nowrap"
                    textTransform="uppercase"
                    letterSpacing="widest"
                    textShadow="0 1px 2px rgba(229, 62, 62, 0.3)"
                  >
                    TARGET
                  </Text>
                </Box>
              </Box>
            );
          })}
        </VStack>

        {/* Insight Box + View All Button */}
        <Flex justify="space-between" align="flex-end" mt={4} gap={4} flexWrap="wrap">
          <Box flex="1" minW="300px">
            <InsightBox type={insight.type} title={insight.title}>
              {insight.message}
            </InsightBox>
          </Box>
          {onViewAll && (
            <Button
              size="md"
              variant="outline"
              colorScheme="blue"
              onClick={onViewAll}
              flexShrink={0}
              borderRadius="full"
              fontWeight="bold"
              px={6}
              boxShadow="md"
              borderWidth="2px"
              _hover={{
                transform: 'translateY(-2px)',
                shadow: 'lg',
                bg: 'blue.50',
              }}
              transition="all 0.2s ease"
            >
              View All {rankings?.length || 20} Rankings
            </Button>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const SetGoalsPage = ({ demoMode, demoData } = {}) => {
  const navigate = useNavigate();
  const { favoriteBusinesses } = useUser();

  // Color mode values - all hooks must be called unconditionally at the top
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const sliderTrackBg = useColorModeValue('gray.200', 'gray.600');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const blueHighlightBg = useColorModeValue('blue.50', 'blue.900');

  // State
  const [reviewsData, setReviewsData] = useState([]);
  const [yearData, setYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [targetPosition, setTargetPosition] = useState(5);
  
  // Modal state for rankings
  const { isOpen: isRankingsOpen, onOpen: onRankingsOpen, onClose: onRankingsClose } = useDisclosure();
  const [activeRankingModal, setActiveRankingModal] = useState({
    title: '',
    rankings: [],
    target: 0,
    unit: '',
    metricKey: '',
  });

  // Fetch data on mount
  useEffect(() => {
    if (demoMode && demoData) {
      setReviewsData(demoData.reviewsData || []);
      setYearData(demoData.yearData || []);
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [reviewsResult, yearResult] = await Promise.all([
          getReviews(),
          getYearData(),
        ]);
        if (reviewsResult) setReviewsData(reviewsResult);
        if (yearResult) setYearData(yearResult);
      } catch (error) {
        logger.error('Error fetching goals data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [demoMode, demoData]);

  // Combine reviews with year data
  const combinedData = useMemo(() => {
    if (!reviewsData.length) return [];
    return reviewsData.map(business => {
      const yearInfo = yearData.find(y => y.maps_uri === business.maps_uri);
      return {
        ...business,
        historicalData: yearInfo?.data || null,
        monthlyReviewCounts: yearInfo?.data?.monthlyReviewCounts || {},
        sentimentBreakdown: yearInfo?.data?.sentimentBreakdown || null,
      };
    });
  }, [reviewsData, yearData]);

  // Calculate review rankings
  const reviewRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .filter(b => b.rating_count > 0)
      .sort((a, b) => b.rating_count - a.rating_count);
  }, [combinedData]);

  // Calculate traffic/SEO rankings
  const trafficRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .filter(b => b.traffic_data?.metrics?.estimated_monthly_visitors > 0)
      .map(b => ({ ...b, seoScore: calculateSeoScore(b) }))
      .sort((a, b) => b.seoScore - a.seoScore);
  }, [combinedData]);

  // Website traffic rankings (monthly organic visits - separate from SEO score)
  const websiteTrafficRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({ ...b, monthlyTraffic: getTrafficValue(b) || 0 }))
      .filter(b => b.monthlyTraffic > 0)
      .sort((a, b) => b.monthlyTraffic - a.monthlyTraffic);
  }, [combinedData]);

  // Full rankings for each metric (for modal and threshold calculation)
  // These are created early so they can be used in targetThresholds
  const monthlyRankingsForThresholds = useMemo(() => {
    return [...combinedData]
      .map(b => ({
        ...b,
        avgMonthlyReviews: Math.round(calculateAvgMonthlyReviews(b) * 10) / 10,
      }))
      .filter(b => b.avgMonthlyReviews > 0)
      .sort((a, b) => b.avgMonthlyReviews - a.avgMonthlyReviews);
  }, [combinedData]);

  const sentimentRankingsForThresholds = useMemo(() => {
    return [...combinedData]
      .map(b => ({
        ...b,
        sentimentScore: Math.round(calculateSentimentScore(b.sentimentBreakdown)),
      }))
      .filter(b => b.sentimentScore > 0)
      .sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  // Calculate target thresholds using INDEPENDENT rankings for each metric
  // Position 1 = highest value, Position 5 = 5th highest value, etc.
  const targetThresholds = useMemo(() => {
    if (!reviewRankings.length) return null;

    // Monthly reviews: get the Nth highest monthly review value (INDEPENDENT)
    const effectiveMonthlyTarget = Math.min(targetPosition, monthlyRankingsForThresholds.length);
    const monthlyTarget = effectiveMonthlyTarget > 0
      ? monthlyRankingsForThresholds[effectiveMonthlyTarget - 1]?.avgMonthlyReviews || 0
      : 0;

    // Total reviews: get the Nth highest total review count (INDEPENDENT)
    const effectiveTotalTarget = Math.min(targetPosition, reviewRankings.length);
    const totalReviewsTarget = effectiveTotalTarget > 0
      ? reviewRankings[effectiveTotalTarget - 1]?.rating_count || 0
      : 0;

    // Traffic/SEO: get the Nth highest SEO score (INDEPENDENT)
    const effectiveTrafficTarget = Math.min(targetPosition, trafficRankings.length);
    const trafficTarget = effectiveTrafficTarget > 0
      ? Math.round(trafficRankings[effectiveTrafficTarget - 1]?.seoScore || 0)
      : 0;

    // Website traffic: get the Nth highest monthly organic visits (INDEPENDENT)
    const effectiveWebTrafficTarget = Math.min(targetPosition, websiteTrafficRankings.length);
    const webTrafficTarget = effectiveWebTrafficTarget > 0
      ? websiteTrafficRankings[effectiveWebTrafficTarget - 1]?.monthlyTraffic || 0
      : 0;

    // Sentiment: get the Nth highest sentiment score (INDEPENDENT)
    const effectiveSentimentTarget = Math.min(targetPosition, sentimentRankingsForThresholds.length);
    const sentimentTarget = effectiveSentimentTarget > 0
      ? sentimentRankingsForThresholds[effectiveSentimentTarget - 1]?.sentimentScore || 85
      : 85;

    return {
      monthlyReviews: monthlyTarget,
      totalReviews: totalReviewsTarget,
      trafficScore: trafficTarget,
      webTraffic: webTrafficTarget,
      sentimentScore: sentimentTarget,
      targetPosition: Math.min(targetPosition, reviewRankings.length),
    };
  }, [reviewRankings, trafficRankings, websiteTrafficRankings, monthlyRankingsForThresholds, sentimentRankingsForThresholds, targetPosition]);

  // Enrich favorites with metrics and rankings
  const enrichedFavorites = useMemo(() => {
    if (!favoriteBusinesses || !favoriteBusinesses.length) return [];

    return favoriteBusinesses.map(fav => {
      const fullData = combinedData.find(b => b.maps_uri === fav.maps_uri) || fav;
      const reviewRankPosition = reviewRankings.findIndex(b => b.maps_uri === fav.maps_uri) + 1;
      const trafficRankPosition = trafficRankings.findIndex(b => b.maps_uri === fav.maps_uri) + 1;
      const webTrafficRankPosition = websiteTrafficRankings.findIndex(b => b.maps_uri === fav.maps_uri) + 1;

      return {
        ...fav,
        ...fullData,
        avgMonthlyReviews: Math.round(calculateAvgMonthlyReviews(fullData) * 10) / 10,
        seoScore: Math.round(calculateSeoScore(fullData)),
        monthlyTraffic: getTrafficValue(fullData) || 0,
        sentimentScore: Math.round(calculateSentimentScore(
          fullData.sentimentBreakdown || fav.sentimentBreakdown
        )),
        reviewRank: reviewRankPosition > 0 ? reviewRankPosition : null,
        trafficRank: trafficRankPosition > 0 ? trafficRankPosition : null,
        webTrafficRank: webTrafficRankPosition > 0 ? webTrafficRankPosition : null,
      };
    });
  }, [favoriteBusinesses, combinedData, reviewRankings, trafficRankings, websiteTrafficRankings]);

  // Chart data
  const allFavoriteNames = useMemo(() => enrichedFavorites.map(fav => fav.name), [enrichedFavorites]);

  const monthlyChartData = useMemo(() => {
    if (!enrichedFavorites.length || !targetThresholds) return [];
    return enrichedFavorites.map(fav => ({
      name: fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name,
      fullName: fav.name,
      value: fav.avgMonthlyReviews,
      target: targetThresholds.monthlyReviews,
      meetsTarget: fav.avgMonthlyReviews >= targetThresholds.monthlyReviews,
    }));
  }, [enrichedFavorites, targetThresholds]);

  const totalReviewsChartData = useMemo(() => {
    if (!enrichedFavorites.length || !targetThresholds) return [];
    return enrichedFavorites.map(fav => ({
      name: fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name,
      fullName: fav.name,
      value: fav.rating_count || 0,
      target: targetThresholds.totalReviews,
      meetsTarget: (fav.rating_count || 0) >= targetThresholds.totalReviews,
    }));
  }, [enrichedFavorites, targetThresholds]);

  const trafficChartData = useMemo(() => {
    if (!enrichedFavorites.length || !targetThresholds) return [];
    return enrichedFavorites.map(fav => ({
      name: fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name,
      fullName: fav.name,
      value: fav.seoScore,
      target: targetThresholds.trafficScore,
      meetsTarget: fav.seoScore >= targetThresholds.trafficScore,
    }));
  }, [enrichedFavorites, targetThresholds]);

  const websiteTrafficChartData = useMemo(() => {
    if (!enrichedFavorites.length || !targetThresholds) return [];
    return enrichedFavorites.map(fav => ({
      name: fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name,
      fullName: fav.name,
      value: fav.monthlyTraffic,
      target: targetThresholds.webTraffic,
      meetsTarget: fav.monthlyTraffic >= targetThresholds.webTraffic,
    }));
  }, [enrichedFavorites, targetThresholds]);

  const sentimentChartData = useMemo(() => {
    if (!enrichedFavorites.length || !targetThresholds) return [];
    return enrichedFavorites.map(fav => ({
      name: fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name,
      fullName: fav.name,
      value: fav.sentimentScore,
      target: targetThresholds.sentimentScore,
      meetsTarget: fav.sentimentScore >= targetThresholds.sentimentScore,
    }));
  }, [enrichedFavorites, targetThresholds]);

  // Full rankings for each metric (for modal)
  const monthlyRankings = useMemo(() => {
    return [...combinedData]
      .map(b => ({
        ...b,
        avgMonthlyReviews: Math.round(calculateAvgMonthlyReviews(b) * 10) / 10,
      }))
      .sort((a, b) => b.avgMonthlyReviews - a.avgMonthlyReviews);
  }, [combinedData]);

  const totalReviewsRankings = useMemo(() => {
    return [...combinedData]
      .filter(b => b.rating_count > 0)
      .sort((a, b) => b.rating_count - a.rating_count);
  }, [combinedData]);

  const seoRankings = useMemo(() => {
    return [...combinedData]
      .map(b => ({ ...b, seoScore: Math.round(calculateSeoScore(b)) }))
      .filter(b => b.seoScore > 0)
      .sort((a, b) => b.seoScore - a.seoScore);
  }, [combinedData]);

  const webTrafficFullRankings = useMemo(() => {
    return [...combinedData]
      .map(b => ({ ...b, monthlyTraffic: getTrafficValue(b) || 0 }))
      .filter(b => b.monthlyTraffic > 0)
      .sort((a, b) => b.monthlyTraffic - a.monthlyTraffic);
  }, [combinedData]);

  const sentimentRankings = useMemo(() => {
    return [...combinedData]
      .map(b => ({
        ...b,
        sentimentScore: Math.round(calculateSentimentScore(b.sentimentBreakdown)),
      }))
      .filter(b => b.sentimentScore > 0)
      .sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  // Handler for opening rankings modal
  const handleViewRankings = useCallback((title, rankings, target, unit, metricKey) => {
    setActiveRankingModal({ title, rankings, target, unit, metricKey });
    onRankingsOpen();
  }, [onRankingsOpen]);

  // Discrete slider positions - clean stops only
  const sliderPositions = [1, 3, 5, 10, 15, 20];
  
  // Get current index from position
  const currentIndex = sliderPositions.indexOf(targetPosition);
  const sliderIndex = currentIndex >= 0 ? currentIndex : 0;

  // Handle slider change - index-based for clean snapping
  const handleSliderChange = useCallback((index) => {
    setTargetPosition(sliderPositions[index]);
  }, []);

  if (isLoading) {
    return (
      <Box bg={pageBg} minH="100vh" p={6}>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Define targets for reviews, sentiment, traffic, and market position. Track progress and get alerts when you hit milestones.">
          Set Goals
        </SectionLabel>}

        <Flex justify="center" align="center" minH="400px" direction="column">
          <Spinner size="xl" thickness="4px" color="blue.500" mb={4} />
          <Text color={subtleTextColor}>Loading goals data...</Text>
        </Flex>
      </Box>
    );
  }

  if (!reviewsData.length) {
    return (
      <Box bg={pageBg} minH="100vh" p={6}>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Define targets for reviews, sentiment, traffic, and market position. Track progress and get alerts when you hit milestones.">
          Set Goals
        </SectionLabel>}

        <Flex justify="center" align="center" minH="400px" direction="column">
          <Icon as={FaBullseye} boxSize={12} color="gray.400" mb={4} />
          <Text color={subtleTextColor}>No data available to set goals.</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh" p={6}>
      {/* Section Label */}
      {!demoMode && <SectionLabel description="Define targets for reviews, sentiment, traffic, and market position. Track progress and get alerts when you hit milestones.">
        Set Goals
      </SectionLabel>}

      <Box w="100%">
        {/* Goal Slider Section */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} shadow="md" mb={6}>
          <CardBody p={6}>
            {/* Slider Header */}
            <Flex justify="space-between" align="center" mb={6}>
              <HStack spacing={3}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={6} />
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  Your Target Rank
                </Text>
              </HStack>
              <Badge
                colorScheme="blue"
                fontSize="md"
                px={5}
                py={2}
                borderRadius="full"
                fontWeight="bold"
                boxShadow="0 4px 12px rgba(66, 153, 225, 0.3)"
              >
                Top {targetPosition}
              </Badge>
            </Flex>

            {/* Slider - Clean discrete slider */}
            <Box px={4} mb={6}>
              <Box
                position="relative"
                py={2}
              >
                <Slider
                  min={0}
                  max={sliderPositions.length - 1}
                  step={1}
                  value={sliderIndex}
                  onChange={handleSliderChange}
                >
                  <SliderTrack 
                    bg={sliderTrackBg} 
                    h="12px" 
                    borderRadius="full"
                    boxShadow="inset 0 2px 4px rgba(0,0,0,0.1)"
                  >
                    <SliderFilledTrack 
                      bgGradient="linear(to-r, blue.400, blue.500)"
                      borderRadius="full"
                    />
                  </SliderTrack>
                  <SliderThumb
                    boxSize={6}
                    bg="white"
                    border="3px solid"
                    borderColor="blue.500"
                    boxShadow="md"
                    _focus={{ 
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.4)' 
                    }}
                  />
                </Slider>
              </Box>
              
              {/* Slider Labels - evenly spaced */}
              <HStack justify="space-between" mt={3}>
                {sliderPositions.map((pos) => {
                  const isActive = targetPosition === pos;
                  return (
                    <Text
                      key={pos}
                      fontSize="sm"
                      fontWeight={isActive ? 'bold' : 'medium'}
                      color={isActive ? 'blue.500' : subtleTextColor}
                      cursor="pointer"
                      onClick={() => setTargetPosition(pos)}
                      px={2}
                      py={1}
                      borderRadius="md"
                      bg={isActive ? 'blue.50' : 'transparent'}
                      transition="color 0.15s"
                    >
                      #{pos}
                    </Text>
                  );
                })}
              </HStack>
            </Box>

            {/* Current Favorites Positions */}
            {enrichedFavorites.length > 0 && (
              <Box
                bg={blueHighlightBg}
                borderRadius="xl"
                p={5}
                mt={4}
                boxShadow="md"
                border="1px solid"
                borderColor="blue.100"
              >
                <HStack mb={3}>
                  <Text fontSize="lg">💝</Text>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                    Your Favorites' Current Positions
                  </Text>
                </HStack>
                <Flex wrap="wrap" gap={3}>
                  {enrichedFavorites.map((fav, idx) => (
                    <Tooltip
                      key={idx}
                      label={`Reviews: #${fav.reviewRank || 'N/A'} • Traffic: #${fav.trafficRank || 'N/A'}`}
                      hasArrow
                    >
                      <HStack
                        bg={cardBg}
                        px={3}
                        py={2}
                        borderRadius="full"
                        borderWidth="2px"
                        borderColor={fav.reviewRank && fav.reviewRank <= targetPosition ? 'green.400' : borderColor}
                        spacing={2}
                        cursor="default"
                        _hover={{ shadow: 'md' }}
                        transition="all 0.2s"
                      >
                        <Badge
                          colorScheme={fav.reviewRank && fav.reviewRank <= targetPosition ? 'green' : 'gray'}
                          fontSize="sm"
                          borderRadius="full"
                          px={2}
                        >
                          #{fav.reviewRank || '—'}
                        </Badge>
                        <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                          {fav.name?.length > 20 ? fav.name.substring(0, 20) + '...' : fav.name}
                        </Text>
                        {fav.reviewRank && fav.reviewRank <= targetPosition && (
                          <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        )}
                      </HStack>
                    </Tooltip>
                  ))}
                </Flex>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Benchmark Cards */}
        {targetThresholds && (
          <Box mb={6}>
            <Text fontSize="sm" fontWeight="bold" color={subtleTextColor} mb={4} textTransform="uppercase" letterSpacing="wider">
              What You Need to Hit Top {targetPosition}
            </Text>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
              <BenchmarkCard
                icon={FaCalendarAlt}
                label="Monthly Reviews"
                value={targetThresholds.monthlyReviews}
                unit="reviews/month"
                color="teal"
              />
              <BenchmarkCard
                icon={FaStar}
                label="Total Reviews"
                value={targetThresholds.totalReviews.toLocaleString()}
                unit="reviews"
                color="orange"
              />
              <BenchmarkCard
                icon={FaGlobe}
                label="Website Traffic"
                value={targetThresholds.webTraffic > 0 ? formatNumber(targetThresholds.webTraffic) : '—'}
                unit="visits/month"
                color="purple"
              />
              <BenchmarkCard
                icon={FaSearch}
                label="SEO Score"
                value={targetThresholds.trafficScore}
                unit="SEO score"
                color="blue"
              />
              <BenchmarkCard
                icon={FaSmile}
                label="Sentiment"
                value={`${targetThresholds.sentimentScore}%`}
                unit="positive"
                color="green"
              />
            </SimpleGrid>
          </Box>
        )}

        {/* Favorites vs Targets Charts */}
        {enrichedFavorites.length === 0 ? (
          <Alert
            status="info"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            py={10}
            borderRadius="xl"
            bg={blueHighlightBg}
          >
            <AlertIcon boxSize={12} mr={0} mb={4} />
            <AlertTitle mb={2} fontSize="xl">No Favorites Selected</AlertTitle>
            <AlertDescription maxW="md" mb={6} fontSize="md">
              Add businesses to your favorites to see how they compare against your target thresholds.
            </AlertDescription>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={() => navigate('/dashboards/favorites')}
              rightIcon={<Icon as={FaArrowRight} />}
            >
              Go to Favorites
            </Button>
          </Alert>
        ) : (
          <VStack spacing={6} mb={6}>
            {/* Monthly Reviews */}
            <HorizontalBarChart
              data={monthlyChartData}
              target={targetThresholds?.monthlyReviews || 0}
              title="Monthly Review Frequency"
              subtitle={`Avg reviews/month needed for Top ${targetPosition}`}
              icon={FaCalendarAlt}
              iconColor="teal.500"
              unit=""
              allFavoriteNames={allFavoriteNames}
              enrichedFavorites={enrichedFavorites}
              targetPosition={targetPosition}
              rankings={monthlyRankings}
              metricKey="avgMonthlyReviews"
              onViewAll={() => handleViewRankings(
                'Monthly Review Frequency Rankings',
                monthlyRankings,
                targetThresholds?.monthlyReviews || 0,
                '',
                'avgMonthlyReviews'
              )}
            />

            {/* Total Reviews */}
            <HorizontalBarChart
              data={totalReviewsChartData}
              target={targetThresholds?.totalReviews || 0}
              title="Total Reviews"
              subtitle={`Total reviews needed to reach Top ${targetPosition}`}
              icon={FaStar}
              iconColor="orange.500"
              unit=""
              allFavoriteNames={allFavoriteNames}
              enrichedFavorites={enrichedFavorites}
              targetPosition={targetPosition}
              rankings={totalReviewsRankings}
              metricKey="rating_count"
              onViewAll={() => handleViewRankings(
                'Total Reviews Rankings',
                totalReviewsRankings,
                targetThresholds?.totalReviews || 0,
                '',
                'rating_count'
              )}
            />

            {/* Website Traffic */}
            <HorizontalBarChart
              data={websiteTrafficChartData}
              target={targetThresholds?.webTraffic || 0}
              title="Website Traffic"
              subtitle={`Monthly organic visits needed for Top ${targetPosition}`}
              icon={FaGlobe}
              iconColor="purple.500"
              unit=""
              allFavoriteNames={allFavoriteNames}
              enrichedFavorites={enrichedFavorites}
              targetPosition={targetPosition}
              rankings={webTrafficFullRankings}
              metricKey="monthlyTraffic"
              onViewAll={() => handleViewRankings(
                'Website Traffic Rankings',
                webTrafficFullRankings,
                targetThresholds?.webTraffic || 0,
                '',
                'monthlyTraffic'
              )}
            />

            {/* SEO Score */}
            <HorizontalBarChart
              data={trafficChartData}
              target={targetThresholds?.trafficScore || 0}
              title="SEO Score"
              subtitle={`SEO score (visitors/rank) needed for Top ${targetPosition}`}
              icon={FaSearch}
              iconColor="blue.500"
              unit=""
              allFavoriteNames={allFavoriteNames}
              enrichedFavorites={enrichedFavorites}
              targetPosition={targetPosition}
              rankings={seoRankings}
              metricKey="seoScore"
              onViewAll={() => handleViewRankings(
                'SEO Score Rankings',
                seoRankings,
                targetThresholds?.trafficScore || 0,
                '',
                'seoScore'
              )}
            />

            {/* Sentiment Score */}
            <HorizontalBarChart
              data={sentimentChartData}
              target={targetThresholds?.sentimentScore || 0}
              title="Sentiment Score"
              subtitle={`Average sentiment of Top ${targetPosition} businesses`}
              icon={FaSmile}
              iconColor="green.500"
              unit="%"
              allFavoriteNames={allFavoriteNames}
              enrichedFavorites={enrichedFavorites}
              targetPosition={targetPosition}
              rankings={sentimentRankings}
              metricKey="sentimentScore"
              onViewAll={() => handleViewRankings(
                'Sentiment Score Rankings',
                sentimentRankings,
                targetThresholds?.sentimentScore || 0,
                '%',
                'sentimentScore'
              )}
            />
          </VStack>
        )}

        {/* Action Panel CTA */}
        {enrichedFavorites.length > 0 && (
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            shadow="sm"
            borderRadius="lg"
            mb={6}
          >
            <CardBody p={6}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align="center"
                gap={4}
              >
                <HStack spacing={4}>
                  <Flex
                    w="48px"
                    h="48px"
                    borderRadius="lg"
                    bg="blue.50"
                    justify="center"
                    align="center"
                  >
                    <Icon as={FaLightbulb} color="blue.500" boxSize={6} />
                  </Flex>
                  <VStack align={{ base: 'center', md: 'start' }} spacing={0}>
                    <Heading size="sm" color={textColor}>
                      Ready to Close the Gap?
                    </Heading>
                    <Text color={subtleTextColor} fontSize="sm">
                      See the playbook for your favorites to reach Top {targetPosition}
                    </Text>
                  </VStack>
                </HStack>
                <Button
                  colorScheme="blue"
                  rightIcon={<Icon as={FaArrowRight} />}
                  onClick={() => navigate('/playbook/actions')}
                  _hover={{ transform: 'translateX(2px)' }}
                  transition="all 0.2s"
                >
                  View Playbook
                </Button>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Footer Stats */}
        <Flex justify="center" py={4}>
          <Text fontSize="sm" color={subtleTextColor}>
            Based on {reviewRankings.length} businesses in your market
            {enrichedFavorites.length > 0 && ` • Tracking ${enrichedFavorites.length} favorite${enrichedFavorites.length > 1 ? 's' : ''}`}
          </Text>
        </Flex>
      </Box>

      {/* Rankings Modal */}
      <RankingsModal
        isOpen={isRankingsOpen}
        onClose={onRankingsClose}
        title={activeRankingModal.title}
        rankings={activeRankingModal.rankings}
        target={activeRankingModal.target}
        unit={activeRankingModal.unit}
        targetPosition={targetPosition}
        metricKey={activeRankingModal.metricKey}
      />
    </Box>
  );
};

export default SetGoalsPage;
